import { Option } from '@sapphire/result';
import { Nullable } from '@ps/utils';
import {
  mapSurveyLocalization,
  Survey,
  SurveyClassification,
  SurveyLocaleData,
} from '@ps/survey-utils';
import { SurveyChildKey } from './survey-creation-shared-state.model';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from './survey-creation-state.model';
import { DEFAULT_SURVEY_CHILD_KEY } from './survey-creation-state.constants';

/**
 * Per-child map mechanics shared by the `SurveyChildKey`-keyed slices (core, audience, insights,
 * launch); each knows only its own map, never another slice's shape.
 */

export type SurveyChildEntry = readonly [
  SurveyChildKey,
  SurveyCreationChildAudienceState,
  SurveyChildRegistryState,
];

/** A child's key: the survey id once created, else a locale key, else empty. */
export function deriveChildKey(
  surveyId: Nullable<number>,
  locale?: SurveyLocaleData,
): SurveyChildKey {
  return (
    surveyId ??
    (locale
      ? mapSurveyLocalization(locale.countryCode, locale.languageShortCode)
      : '')
  );
}

/** Keeps each existing child, else seeds it via `makeDefaultChild(arg)`. */
function buildKeyedChildren<T, TArg>(
  children: Record<SurveyChildKey, T>,
  keyedArgs: readonly (readonly [SurveyChildKey, TArg])[],
  makeDefaultChild: (arg: TArg) => T,
): Record<SurveyChildKey, T> {
  return Object.fromEntries(
    keyedArgs.map(([key, arg]) => [
      key,
      children[key] ?? makeDefaultChild(arg),
    ]),
  );
}

/**
 * Pre-draft: one child per locale, or one at the default key before any locale. `makeDefaultChild`
 * gets the locale to seed per-locale content, or nothing for the no-locale case.
 */
export function buildLocaleKeyedChildren<T>(
  children: Record<SurveyChildKey, T>,
  localeData: SurveyLocaleData[],
  defaultKey: SurveyChildKey,
  makeDefaultChild: (locale?: SurveyLocaleData) => T,
): Record<SurveyChildKey, T> {
  if (!localeData.length) {
    return buildKeyedChildren(
      children,
      [[defaultKey, undefined]],
      makeDefaultChild,
    );
  }

  return buildKeyedChildren(
    children,
    localeData.map((locale) => [deriveChildKey(null, locale), locale] as const),
    makeDefaultChild,
  );
}

/** Pre-draft tracker: one child per wave ("1".."N"), all seeded from the same audience form. */
export function buildWaveKeyedChildren<T>(
  children: Record<SurveyChildKey, T>,
  numberOfWaves: number,
  makeDefaultChild: () => T,
): Record<SurveyChildKey, T> {
  return buildKeyedChildren(
    children,
    Array.from({ length: numberOfWaves }, (_, index) => [
      String(index + 1),
      undefined,
    ]),
    makeDefaultChild,
  );
}

/** Moves a child from `fromKey` to the default key; no-op if already there or missing. */
export function delegateRecordToDefaultChild<T>(
  children: Record<SurveyChildKey, T>,
  fromKey: SurveyChildKey,
): Record<SurveyChildKey, T> {
  if (fromKey === DEFAULT_SURVEY_CHILD_KEY || !(fromKey in children)) {
    return children;
  }

  const { [fromKey]: delegatedChild, ...rest } = children;
  return { ...rest, [DEFAULT_SURVEY_CHILD_KEY]: delegatedChild };
}

export function collapseToDefaultChild<T>(
  children: Record<SurveyChildKey, T>,
): Record<SurveyChildKey, T> {
  return Option.from(Object.values(children)[0]).mapOr(
    children,
    (firstChild) => ({
      [DEFAULT_SURVEY_CHILD_KEY]: firstChild,
    }),
  );
}

/** The single child a per-child *read* should resolve to. */
export function resolveActiveChildKey<T>(
  children: Record<SurveyChildKey, T>,
  activeChildKey: Nullable<SurveyChildKey>,
  defaultKey: SurveyChildKey,
): SurveyChildKey {
  return activeChildKey ?? Object.keys(children)[0] ?? defaultKey;
}

/**
 * Which children a write targets, chosen explicitly per action. `ACTIVE` falls back to every child
 * when none is active yet (pre-draft) — a deliberate policy, not a side effect of a null key.
 * `ALL_PARTICIPANTS` skips opted-out children; `ALL` ignores participation (the flag itself,
 * registry housekeeping).
 */
export type SurveyChildScope =
  | { kind: 'active' }
  | { kind: 'all' }
  | { kind: 'all-participants' }
  | { kind: 'keys'; keys: readonly SurveyChildKey[] };

export const CHILD_SCOPE = {
  ACTIVE: { kind: 'active' } as const,
  ALL: { kind: 'all' } as const,
  ALL_PARTICIPANTS: { kind: 'all-participants' } as const,
  keys: (keys: readonly SurveyChildKey[]): SurveyChildScope => ({
    kind: 'keys',
    keys,
  }),
};

/** An action payload with a scope; facades default it to `CHILD_SCOPE.ACTIVE`. */
export type WithScope<T> = T & { scope: SurveyChildScope };

/** Picks a scope from the survey `kind`, e.g. metadata reaches every grouped MC child. */
export type ChildScopeRule = (
  kind: Nullable<SurveyClassification>,
) => SurveyChildScope;

// Only core carries `participant`; other slices use core's participantKeys (empty = everyone).
export interface ChildScopeContext {
  readonly activeChildKey: Nullable<SurveyChildKey>;
  readonly participantKeys: readonly SurveyChildKey[];
}

export function resolveScopedChildKeys<T>(
  children: Record<SurveyChildKey, T>,
  context: ChildScopeContext,
  scope: SurveyChildScope,
): SurveyChildKey[] {
  switch (scope.kind) {
    case 'active':
      return Option.from(context.activeChildKey)
        .map((key) => [key])
        .unwrapOr(Object.keys(children));
    case 'all-participants':
      return Object.keys(children).filter((key) =>
        isParticipant(children[key], key, context.participantKeys),
      );
    case 'all':
      return Object.keys(children);

    case 'keys':
      return [...scope.keys];
  }
}

export function participantRecordKeys<T>(
  children: Record<SurveyChildKey, T>,
): ReadonlySet<SurveyChildKey> {
  return new Set(
    resolveScopedChildKeys(
      children,
      { activeChildKey: null, participantKeys: [] },
      CHILD_SCOPE.ALL_PARTICIPANTS,
    ),
  );
}

// A core child's own flag decides (null takes part); other slices' children need core to list them.
function isParticipant(
  child: unknown,
  key: SurveyChildKey,
  participantKeys: readonly SurveyChildKey[],
): boolean {
  if (typeof child === 'object' && child !== null && 'participant' in child) {
    return child.participant !== false;
  }
  return !participantKeys.length || participantKeys.includes(key);
}

export type ChildMapPatch<T> =
  | Partial<T>
  | ((child: T, key: SurveyChildKey) => Partial<T>);

/** Patches one child's fields, applying a static or per-child function patch. */
function patchChild<T>(
  child: T,
  key: SurveyChildKey,
  patch: ChildMapPatch<T>,
): T {
  const patchValues = typeof patch === 'function' ? patch(child, key) : patch;
  return { ...child, ...patchValues };
}

export function patchChildrenMap<T>(
  children: Record<SurveyChildKey, T>,
  context: ChildScopeContext,
  scope: SurveyChildScope,
  patch: ChildMapPatch<T>,
  defaultChild: T,
): Record<SurveyChildKey, T> {
  const patchedChildren = { ...children };

  for (const key of resolveScopedChildKeys(children, context, scope)) {
    const child = children[key] ?? defaultChild;
    patchedChildren[key] = patchChild(child, key, patch);
  }

  return patchedChildren;
}

export function overlayRefreshedChildren<T>(
  children: Record<SurveyChildKey, T>,
  surveys: readonly Survey[],
  keyedLoaded: (surveys: readonly Survey[]) => Record<SurveyChildKey, T>,
): Record<SurveyChildKey, T> {
  return { ...children, ...keyedLoaded(surveys) };
}
